"use client";

// LaborSearch.jsx — standalone, self-contained labor item picker.
// Import into ModuleBuilderPage.jsx with:
//   import LaborSearch, { invalidateLaborCache } from "./LaborSearch";

import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { searchLaborItems, tokenise } from "./laborSearchEngine.js";

const API = "https://erp-xe9u.onrender.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ── Minimal local Icon/Spinner so this file has no dependency on
//    ModuleBuilderPage's icon set ──────────────────────────────────────────
const Icon = ({ d, size = 16, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path fillRule="evenodd" clipRule="evenodd" d={d} />
  </svg>
);

const ICONS = {
  search:
    "M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z",
};

const Spinner = ({ size = 16 }) => (
  <div
    className="border-2 border-orange-500 border-t-transparent rounded-full animate-spin shrink-0"
    style={{ width: size, height: size }}
  />
);

// ── In-memory cache so re-mounting the search box doesn't re-fetch the
//    whole labor catalog every time ─────────────────────────────────────────
let _laborCache = null; // { items, fetchedAt }
let _laborFetchPromise = null;
const LABOR_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

async function loadLaborItems(token) {
  const fresh =
    _laborCache && Date.now() - _laborCache.fetchedAt < LABOR_CACHE_TTL_MS;
  if (fresh) return _laborCache.items;

  if (_laborFetchPromise) return _laborFetchPromise;

  _laborFetchPromise = axios
    .get(`${API}/item-database`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { category: "L", isActive: true, limit: 5000 },
    })
    .then((res) => {
      const raw =
        res.data.items ||
        res.data.data ||
        (Array.isArray(res.data) ? res.data : []);
      const items = raw.filter((i) => !i.category || i.category === "L");
      _laborCache = { items, fetchedAt: Date.now() };
      return items;
    })
    .finally(() => {
      _laborFetchPromise = null;
    });

  return _laborFetchPromise;
}

// Call after adding/editing an item in ItemDatabaseManager if you want this
// picker to reflect it immediately instead of waiting out the TTL.
export function invalidateLaborCache() {
  _laborCache = null;
}

// ── Component ────────────────────────────────────────────────────────────
export default function LaborSearch({
  token,
  onSelect,
  placeholder,
  autoFocus = false,
  sectionCode = "",
}) {
  const [query, setQuery] = useState("");
  const [allLabor, setAllLabor] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    loadLaborItems(token)
      .then((items) => {
        if (!cancelled) setAllLabor(items);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const results = useMemo(() => {
    if (!allLabor.length) return [];
    return searchLaborItems(allLabor, query, { sectionCode }).slice(0, 12);
  }, [allLabor, query, sectionCode]);

  const showSectionSuggestions = !query.trim() && sectionCode;

  const LaborItem = ({ item, onPick, badge }) => (
    <button
      onClick={() => onPick(item)}
      className="w-full text-left px-2.5 py-2 flex items-center justify-between hover:bg-slate-800/80 transition-colors rounded-lg"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-mono text-sky-300 font-semibold">
            {item.itemCode}
          </span>
          {(item.sectionNumber || item.costCode?.code) && (
            <span className="text-[9px] font-mono bg-slate-800 text-slate-400 px-1 py-0.5 rounded border border-slate-700">
              §{item.sectionNumber || item.costCode?.code}
            </span>
          )}
          {badge && (
            <span className="text-[9px] bg-violet-500/25 text-violet-200 border border-violet-500/40 px-1.5 py-0.5 rounded font-semibold">
              {badge}
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-100 truncate mt-0.5">
          {item.description || item.itemName}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-2">
        <span className="text-[10px] text-slate-400">{item.uom}</span>
        <div className="text-right">
          <div className="text-[10px] text-emerald-300 font-mono font-semibold">
            ${fmt(item.defaultCost)}
          </div>
        </div>
      </div>
    </button>
  );

  return (
    <div className="relative space-y-2">
      <div className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-900 border border-sky-600/50 rounded-lg focus-within:border-sky-400 transition-colors">
        <Icon d={ICONS.search} size={13} className="text-sky-400 shrink-0" />
        <input
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            loading
              ? "Loading labor items…"
              : placeholder ||
                "Search labor items (code, description, gauge, size)…"
          }
          disabled={loading}
          className="flex-1 text-xs bg-transparent text-slate-100 placeholder-slate-500 focus:outline-none disabled:opacity-50"
        />
        {loading && <Spinner size={13} />}
        {query && !loading && (
          <button
            onClick={() => setQuery("")}
            className="text-slate-500 hover:text-slate-300 text-base leading-none"
          >
            ×
          </button>
        )}
      </div>

      {loadError && (
        <p className="text-[10px] text-rose-400 px-1">
          Couldn't load labor items — try again.
        </p>
      )}

      {!loading &&
        !loadError &&
        (results.length > 0 || showSectionSuggestions) && (
          <div className="absolute top-full left-0 right-0 z-30 mt-0.5 bg-slate-950 border border-slate-700 rounded-xl shadow-2xl max-h-56 overflow-y-auto divide-y divide-slate-800/60">
            {results.length === 0 ? (
              <p className="px-3 py-3 text-[11px] text-slate-500">
                No labor items match "{query}"
              </p>
            ) : (
              results.map((item) => (
                <LaborItem
                  key={item._id || item.itemCode}
                  item={item}
                  badge={
                    showSectionSuggestions ||
                    (sectionCode &&
                      (item.sectionNumber || item.costCode?.code) ===
                        sectionCode)
                      ? "§ match"
                      : null
                  }
                  onPick={(i) => {
                    onSelect(i);
                    setQuery("");
                  }}
                />
              ))
            )}
          </div>
        )}
    </div>
  );
}
